import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import { playMessageSound, playMentionSound } from "../../lib/sound";
import { useRealtime } from "../../contexts/RealtimeContext";
import { logWarn } from "../../lib/logger";

export function useChatMessages(channel, userId) {
  const { subscribe } = useRealtime();
  const [messages, setMessages] = useState([]);
  const lastMsgIdRef = useRef(null);
  const chatInitRef = useRef(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/chat/messages", { params: { channel } });
    setMessages(data);
    if (data.length === 0) return;
    const last = data[data.length - 1];
    if (!chatInitRef.current) {
      chatInitRef.current = true;
      lastMsgIdRef.current = last.id;
      return;
    }
    if (last.id !== lastMsgIdRef.current) {
      const prevIdx = data.findIndex((m) => m.id === lastMsgIdRef.current);
      const newMsgs = prevIdx === -1 ? [] : data.slice(prevIdx + 1);
      lastMsgIdRef.current = last.id;
      const incoming = newMsgs.filter((m) => m.user_id !== userId);
      if (incoming.length > 0) {
        if (incoming.some((m) => (m.mentions || []).includes(userId))) playMentionSound();
        else playMessageSound();
      }
    }
  }, [channel, userId]);

  useEffect(() => {
    chatInitRef.current = false;
    lastMsgIdRef.current = null;
    const onFail = (err) => logWarn("Chat messages load failed:", err);
    load().catch(onFail);
    const unsubscribe = subscribe((evt) => {
      if (evt.type === "message" && evt.channel === channel) load().catch(onFail);
    });
    const t = setInterval(() => load().catch(onFail), 60000); // SSE fallback
    return () => { unsubscribe(); clearInterval(t); };
  }, [load, subscribe, channel]);

  const editMessage = useCallback(async (id, text, mentions) => {
    const { data } = await api.patch(`/chat/messages/${id}`, { text, mentions });
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...data } : m)));
  }, []);

  const deleteMessage = useCallback(async (id) => {
    await api.delete(`/chat/messages/${id}`);
    setMessages((prev) => prev.map((m) => (m.id === id
      ? { ...m, is_deleted: true, text: "", mentions: [], attachments: [] } : m)));
  }, []);

  return { messages, reload: load, editMessage, deleteMessage };
}
